import type { GraphMetadata, Graph } from "@lr-notitia/notitia-operator/schemas";
import { api } from "@lr-notitia/notitia-operator/client";
import { ref } from "vue";
import { defineStore } from "pinia";
import { newGraph, handleGraphModification } from "./graph/utils";

import { datatypes, datatype } from "./graph/datatype";
import { enumItems, enumItem } from "./graph/enumItem";
import { notifications, notification, hiddenNotifications } from "./graph/notification";
import { conceptDataobjects, conceptDataobject } from "./graph/c-dataobject";
import { conceptAttributes, conceptAttribute } from "./graph/c-attribute";
import { templateDataspaces, templateDataspace } from "./graph/t-dataspace";
import { templateWorkspaces, templateWorkspace } from "./graph/t-workspace";
import { templateProcesses, templateProcess } from "./graph/t-process";
import { templateDataobjects, templateDataobject } from "./graph/t-dataobject";
import { templateTasks, templateTask } from "./graph/t-task";
import { instanceDataobjects, instanceDataobject } from "./graph/i-dataobject";
import { instanceAttributes, instanceAttribute } from "./graph/i-attribute";
import { instanceTasks, instanceTask } from "./graph/i-task";

export const useGraphStore = defineStore("graph", () => {
	const domainId = ref("");
	const workspaceId = ref("");
	const graph = ref<Graph>(newGraph());
	const metadata = ref<GraphMetadata>({
		totalThoughts: 0,
		cycleThoughts: 0,
		computationTime: 0,
	});

	function loadGraph(dId: string, wsId: string): Promise<Graph> {
		domainId.value = dId;
		workspaceId.value = wsId;
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId.value)
				.graphs(wsId)
				.get()
				.then((data) => {
					graph.value = data;
					resolve(data);
					api()
						.socket()
						.domain(domainId.value)
						.graph(wsId)
						.on((message) => handleGraphModification(message.data));
				})
				.catch(() => reject("Failed to load graph"));
		});
	}
	function clearGraph(wsId: string) {
		api().socket().domain(domainId.value).graph(wsId).off();
		graph.value = newGraph();
		metadata.value = {
			totalThoughts: 0,
			cycleThoughts: 0,
			computationTime: 0,
		};
		domainId.value = "";
		workspaceId.value = "";
	}

	return {
		domainId,
		workspaceId,
		graph,
		metadata,
		loadGraph,
		clearGraph,
		descriptor: {
			datatypes: datatypes(graph),
			datatype: datatype(domainId, graph),
			notifications: notifications(graph),
			notification: notification(domainId, graph),
			hiddenNotifications: hiddenNotifications,
			enumItems: enumItems(graph),
			enumItem: enumItem(domainId, graph),
		},
		concept: {
			dataobjects: conceptDataobjects(graph),
			dataobject: conceptDataobject(domainId, graph),
			attributes: conceptAttributes(graph),
			attribute: conceptAttribute(domainId, graph),
		},
		template: {
			dataspaces: templateDataspaces(graph),
			dataspace: templateDataspace(domainId, graph),
			workspaces: templateWorkspaces(graph),
			workspace: templateWorkspace(domainId, graph),
			processes: templateProcesses(graph),
			process: templateProcess(domainId, graph),
			dataobjects: templateDataobjects(graph),
			dataobject: templateDataobject(domainId, graph),
			tasks: templateTasks(graph),
			task: templateTask(domainId, graph),
		},
		instance: {
			dataobjects: instanceDataobjects(graph),
			dataobject: instanceDataobject(domainId, graph),
			attributes: instanceAttributes(graph),
			attribute: instanceAttribute(domainId, graph),
			tasks: instanceTasks(graph),
			task: instanceTask(domainId, graph),
		},
	};
});
